import { useState, useEffect, createContext, useContext, useCallback, useMemo } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";

// Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// API helper - created once
const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem("user");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(() => {
    // Only show loading if we have a token but no user yet
    const token = localStorage.getItem("token");
    const saved = localStorage.getItem("user");
    return token && !saved;
  });
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized) return;
    
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    
    if (token && !savedUser) {
      // Token exists but no user - verify token
      api.get("/auth/me")
        .then((res) => {
          setUser(res.data);
          localStorage.setItem("user", JSON.stringify(res.data));
        })
        .catch(() => {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          setUser(null);
        })
        .finally(() => {
          setLoading(false);
          setInitialized(true);
        });
    } else {
      setLoading(false);
      setInitialized(true);
    }
  }, [initialized]);

  // Handle 401 responses
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(interceptor);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", res.data.token);
    localStorage.setItem("user", JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const res = await api.post("/auth/register", { email, password, name });
    localStorage.setItem("token", res.data.token);
    localStorage.setItem("user", JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    login,
    register,
    logout,
    loading,
    api
  }), [user, login, register, logout, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route - simplified
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Import pages
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import Income from "./pages/Income";
import Expenses from "./pages/Expenses";
import RealEstate from "./pages/RealEstate";
import PropertyDetail from "./pages/PropertyDetail";
import RentLedger from "./pages/RentLedger";
import RentDashboard from "./pages/RentDashboard";
import Investments from "./pages/Investments";
import Insurance from "./pages/Insurance";
import AIAssistant from "./pages/AIAssistant";
import Business from "./pages/Business";
import TaxCenter from "./pages/TaxCenter";
import Calendar from "./pages/Calendar";
import AIAdvisor from "./pages/AIAdvisor";
import Automation from "./pages/Automation";
import RealEstateResearch from "./pages/RealEstateResearch";
import CreditCards from "./pages/CreditCards";
import UnitRentLedger from "./pages/UnitRentLedger";
import TenantPortal from "./pages/TenantPortal";
import DealFinder from "./pages/DealFinder";
import Goals from "./pages/Goals";
import NetWorth from "./pages/NetWorth";
import Reports from "./pages/Reports";
import Loans from "./pages/Loans";
import CashFlow from "./pages/CashFlow";
import BudgetPlanner from "./pages/BudgetPlanner";
import RecurringTransactions from "./pages/RecurringTransactions";
import HealthScore from "./pages/HealthScore";
import ReceiptScanner from "./pages/ReceiptScanner";
import FinancialCalendar from "./pages/FinancialCalendar";
import DebtPayoff from "./pages/DebtPayoff";
import TaxEducation from "./pages/TaxEducation";
import AITaxAdvisor from "./pages/AITaxAdvisor";
import AIFormFiller from "./pages/AIFormFiller";
import TaxOCR from "./pages/TaxOCR";

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster 
          theme="dark" 
          position="top-right"
          toastOptions={{
            style: {
              background: '#0A0A0A',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#fff',
            },
          }}
        />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/income" element={<ProtectedRoute><Income /></ProtectedRoute>} />
          <Route path="/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
          <Route path="/loans" element={<ProtectedRoute><Loans /></ProtectedRoute>} />
          <Route path="/real-estate" element={<ProtectedRoute><RealEstate /></ProtectedRoute>} />
          <Route path="/real-estate/:id" element={<ProtectedRoute><PropertyDetail /></ProtectedRoute>} />
          <Route path="/real-estate/:propertyId/ledger" element={<ProtectedRoute><RentLedger /></ProtectedRoute>} />
          <Route path="/real-estate/:propertyId/unit/:unitId/ledger" element={<ProtectedRoute><RentLedger /></ProtectedRoute>} />
          <Route path="/rent-dashboard" element={<ProtectedRoute><RentDashboard /></ProtectedRoute>} />
          <Route path="/business" element={<ProtectedRoute><Business /></ProtectedRoute>} />
          <Route path="/investments" element={<ProtectedRoute><Investments /></ProtectedRoute>} />
          <Route path="/insurance" element={<ProtectedRoute><Insurance /></ProtectedRoute>} />
          <Route path="/tax-center" element={<ProtectedRoute><TaxCenter /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
          <Route path="/ai-assistant" element={<ProtectedRoute><AIAssistant /></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
          <Route path="/ai-advisor" element={<ProtectedRoute><AIAdvisor /></ProtectedRoute>} />
          <Route path="/automation" element={<ProtectedRoute><Automation /></ProtectedRoute>} />
          <Route path="/real-estate-research" element={<ProtectedRoute><RealEstateResearch /></ProtectedRoute>} />
          <Route path="/deal-finder" element={<ProtectedRoute><DealFinder /></ProtectedRoute>} />
          <Route path="/goals" element={<ProtectedRoute><Goals /></ProtectedRoute>} />
          <Route path="/net-worth" element={<ProtectedRoute><NetWorth /></ProtectedRoute>} />
          <Route path="/cash-flow" element={<ProtectedRoute><CashFlow /></ProtectedRoute>} />
          <Route path="/budget" element={<ProtectedRoute><BudgetPlanner /></ProtectedRoute>} />
          <Route path="/recurring" element={<ProtectedRoute><RecurringTransactions /></ProtectedRoute>} />
          <Route path="/health-score" element={<ProtectedRoute><HealthScore /></ProtectedRoute>} />
          <Route path="/receipt-scanner" element={<ProtectedRoute><ReceiptScanner /></ProtectedRoute>} />
          <Route path="/financial-calendar" element={<ProtectedRoute><FinancialCalendar /></ProtectedRoute>} />
          <Route path="/debt-payoff" element={<ProtectedRoute><DebtPayoff /></ProtectedRoute>} />
          <Route path="/tax-education" element={<ProtectedRoute><TaxEducation /></ProtectedRoute>} />
          <Route path="/tax-advisor" element={<ProtectedRoute><AITaxAdvisor /></ProtectedRoute>} />
          <Route path="/form-filler" element={<ProtectedRoute><AIFormFiller /></ProtectedRoute>} />
          <Route path="/tax-ocr" element={<ProtectedRoute><TaxOCR /></ProtectedRoute>} />
          <Route path="/credit-cards" element={<ProtectedRoute><CreditCards /></ProtectedRoute>} />
          <Route path="/unit/:unitId/rent-ledger" element={<ProtectedRoute><UnitRentLedger /></ProtectedRoute>} />
          <Route path="/tenant-portal/:accessToken" element={<TenantPortal />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { api };
